import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquare } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const { data, isLoading } = useGetMyOrdersQuery(undefined, {
    skip: !user,
  });

  // Normalize backend
  const orders = data?.data || data?.transactions || data?.orders || [];

  // Only this user's orders
  const myOrders = orders
    .filter(
      (o) =>
        o?.email === user?.email ||
        String(o?.userId) === String(user?._id)
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // newest first

  /* ---------------- LOADING ---------------- */
  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  /* ---------------- EMPTY STATE ---------------- */
  if (!myOrders.length) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">

        <h2 className="text-2xl font-semibold mb-2">
          No purchases yet 🛒
        </h2>

        <p className="text-gray-500 mb-6 text-center">
          Looks like you haven’t bought any courses yet.
          Start learning today!
        </p>

        <Button onClick={() => navigate("/courses")}>
          Browse Courses
        </Button>

      </div>
    );
  }

  /* ---------------- TABLE UI ---------------- */
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6 min-h-screen bg-gradient-to-b from-gray-50 to-white">

      <h1 className="text-4xl font-bold text-center mb-6">
        🧾 My Orders
      </h1>

      <Table>
        <TableCaption>Your purchase history</TableCaption>

        <TableHeader>
          <TableRow>
            <TableHead>#</TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Transaction</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {myOrders.map((order, idx) => (
            <TableRow key={order._id}>

              <TableCell>{idx + 1}</TableCell>

              <TableCell className="font-medium">
                {order.courseName || "Course"}
              </TableCell>

              <TableCell>
                {new Date(order.createdAt).toLocaleDateString()}
              </TableCell>

              <TableCell>₹{order.amount}</TableCell>

              <TableCell>
                <Badge
                  className={
                    order.status === "SUCCESS"
                      ? "bg-green-600"
                      : order.status === "PENDING"
                      ? "bg-yellow-500"
                      : "bg-red-600"
                  }
                >
                  {order.status}
                </Badge>
              </TableCell>

              <TableCell className="text-xs">
                {order.transactionId || "—"}
              </TableCell>

              <TableCell className="text-right">
                {order.status === "SUCCESS" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate("/dashboard/support")}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Refund
                  </Button>
                )}
              </TableCell>

            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MyOrdersPage;